import React from "react";
import { ImageInfoGalleryItem, Style } from "@glamcorner/gc-types/generated/types";
import { CatalogueEvents } from "@glamcorner/gc-types/generated/ipcBusEvents";
import { centsToDollars } from "../../util";
import { Image } from "../Image";
import { getRenderMode, isClearanceCatalogue, RenderMode } from "../../../client/Catalogue/util";
import { getStyleItemAnalyticsData } from "../../../client/Catalogue/Analytics";
import { CollectionDesignation } from "../../interfaces";
import { Bus } from "../../../client/services/Bus";
import HearIcon from "../../../../assets/favourite-icn.inline.svg";
import "./styleItem.scss";
import { SubscriptionService } from "../../../client/services/SubscriptionService";

export interface StyleItemProps {
  styleItem: Style;
  tag?: (styleItem: Style, collectionDesignation: CollectionDesignation) => string | undefined;
  position: number;
  camelCaseMicroData?: boolean;
  withStyleImageCarousels: boolean;
  hashUrl: string;
  collectionDesignation: CollectionDesignation;
}

const getUrl = (url: string, hashUrl: string) => {
  if (hashUrl) {
    return `${url}#${hashUrl}`;
  }
  return url;
};

const getDataProduct = ({ magento_product_id, style_code, name, try_on_allowed }: Style) =>
  JSON.stringify({
    id: magento_product_id,
    style: style_code,
    name: name,
    disallow_try_on: !try_on_allowed,
  });

const renderGalleryItems = (
  { image_info }: Style,
  camelCaseMicroData: boolean,
  withStyleImageCarousels: boolean,
  eagerLoad: boolean
) => {
  if (!image_info) {
    return null;
  }
  return image_info.gallery.map((imageInfoGalleryItem: ImageInfoGalleryItem, index: number) => {
    const isPrimaryImage = index === 0;
    const label = image_info.label;
    if (withStyleImageCarousels || isPrimaryImage) {
      return (
        <Image
          {...{ imageInfoGalleryItem, label }}
          key={index}
          {...(camelCaseMicroData ? { camelCaseMicroData } : {})}
          eagerLoad={eagerLoad}
        />
      );
    } else {
      return null;
    }
  });
};

/*
 * props.forSubscription determines whether the component is rendered as an on-demand style or a subscription style,
 * in cases where the item is both an on-demand style and a subscription style.
 * If the item is not both, forSubscription will not affect the rendering.
 */
export const StyleItem: React.FunctionComponent<StyleItemProps> = ({
  styleItem,
  position,
  camelCaseMicroData,
  withStyleImageCarousels,
  hashUrl,
  collectionDesignation,
}) => {
  const { url, magento_product_id, style_code, name, designer, print_name } = styleItem;

  const renderMode = getRenderMode(styleItem, collectionDesignation);

  const retailPriceInCents = styleItem.retail_price_in_cents;

  const fourDayRentalPrice = styleItem.rental_prices?.find((rentalPrice) => rentalPrice.rental_days == 4) || null;
  const rentalPrice = fourDayRentalPrice || (styleItem.rental_prices ? styleItem.rental_prices[0] : null);

  const priceInCents =
    renderMode === RenderMode.BUY ? styleItem.buy_price_in_cents : rentalPrice?.rental_price_in_cents || null;

  const discountPriceInCents =
    renderMode === RenderMode.BUY
      ? styleItem.discount_buy_price_in_cents
      : rentalPrice?.discount_rental_price_in_cents || null;
  const priceDescription = renderMode === RenderMode.BUY ? "to buy" : "rental";

  const isDiscounted = !!discountPriceInCents;

  const shouldRenderPrice = !!priceInCents && renderMode !== RenderMode.SUBSCRIPTION;

  const urlLink = getUrl(url, hashUrl);

  // we want to eager load the first 3 products since they are generally the LCP of the page
  // Because on desktop, any of the first 3 images could be randomly selected for CLP metric
  const eagerLoad = position <= 3;

  const onStyleItemClick = () => {
    const isClearance = isClearanceCatalogue();
    const styleItemAnalyticsData = getStyleItemAnalyticsData(styleItem, collectionDesignation, isClearance, hashUrl);

    Bus.broadcast(CatalogueEvents.StyleItemClicked, styleItemAnalyticsData);
  };

  const minimumTier = renderMode === RenderMode.SUBSCRIPTION && SubscriptionService.minimumTier(styleItem.tiers);

  return (
    <div className="c-style-item" data-product={getDataProduct(styleItem)} data-sku={style_code}>
      <meta itemProp="position" content={`${position}`} />

      <a href={urlLink} onClick={onStyleItemClick}>
        <div
          id={`gallery${magento_product_id}`}
          className={
            withStyleImageCarousels
              ? "c-style-item__image grid-item__slides js-grid-item__slides owl-carousel"
              : "c-style-item__image"
          }
        >
          <div className="heart-wrapper js-heart">
            <HearIcon className="nc-icon outline" />
          </div>

          {minimumTier && <div className="c-style__subscription-tag">{minimumTier}</div>}
          {renderGalleryItems(styleItem, !!camelCaseMicroData, withStyleImageCarousels, eagerLoad)}
        </div>
      </a>

      {/* TODO: MAYBE this should go to the designer page, no? */}
      <div className="product-grid-item__text product-grid-item__designer-name">
        <a href={urlLink} onClick={onStyleItemClick}>
          {designer}
        </a>
      </div>

      <div className="product-grid-item__text product-grid-item__product-name">
        <a className="grid-item__link product-grid-item__product-name-text" href={urlLink} onClick={onStyleItemClick}>
          {name}
          {print_name && (
            <span className="product-grid-item__text product-grid-item__print-name-hyphen">{` - ${print_name}`}</span>
          )}
        </a>
      </div>

      {print_name && (
        <a className="product-grid-item__text product-grid-item__print-name" href={urlLink} onClick={onStyleItemClick}>
          {print_name}
        </a>
      )}

      <div className="product-grid-item__text product-grid-item__prices">
        {shouldRenderPrice && (
          <span className={`product-grid-item__price ${isDiscounted ? "product-price-was" : ""}`}>
            ${centsToDollars(priceInCents)} {priceDescription}
          </span>
        )}
        <span className="product-grid-item__price product-grid-item__price--retail sub-text">
          ${centsToDollars(retailPriceInCents)} retail
        </span>

        {isDiscounted && shouldRenderPrice && (
          <div className="product-grid-item__price product-price-now">
            ${centsToDollars(discountPriceInCents, 2)} {priceDescription}
          </div>
        )}
      </div>
    </div>
  );
};
